/**
 * ResourceTable Component
 * Specialized table for GoldenWings resources (airplanes, airports, instructors, students)
 */

import React from 'react';
import { Table, Tag, Space, Button, Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Resource, ResourceType } from '@/api/types/calendars.types';
import './ResourceTable.css';

interface ResourceTableProps {
  /** Resource type to display */
  resourceType: ResourceType;
  /** Table data */
  resources: Resource[];
  /** Loading state */
  loading?: boolean;
  /** Callback for edit action */
  onEdit?: (resource: Resource) => void;
  /** Callback for delete action */
  onDelete?: (resource: Resource) => void;
  /** Callback for toggle availability */
  onToggleAvailability?: (resource: Resource) => void;
}

/**
 * Format resource metadata for display
 */
const formatMetadata = (resource: Resource): React.ReactNode => {
  if (!resource.metadata) return null;

  const metadata = resource.metadata;

  // Airplane metadata
  if (resource.resource_type === 'AIRPLANE') {
    return (
      <Space direction="vertical" size={0}>
        {metadata.registration && (
          <span className="metadata-item">
            <strong>Reg:</strong> {metadata.registration}
          </span>
        )}
        {metadata.make && metadata.model && (
          <span className="metadata-item">
            {metadata.make} {metadata.model}
          </span>
        )}
        {metadata.hourly_rate && (
          <span className="metadata-item">
            <strong>Rate:</strong> ${metadata.hourly_rate}/hr
          </span>
        )}
      </Space>
    );
  }

  // Instructor metadata
  if (resource.resource_type === 'INSTRUCTOR') {
    return (
      <Space direction="vertical" size={0}>
        {metadata.certification && (
          <Tag color="blue">{metadata.certification}</Tag>
        )}
        {metadata.rating && <Tag color="green">{metadata.rating}</Tag>}
        {metadata.hourly_rate && (
          <span className="metadata-item">
            <strong>Rate:</strong> ${metadata.hourly_rate}/hr
          </span>
        )}
      </Space>
    );
  }

  // Airport metadata
  if (resource.resource_type === 'AIRPORT') {
    return (
      <Space direction="vertical" size={0}>
        {metadata.airport_code && (
          <Tag color="orange">{metadata.airport_code}</Tag>
        )}
        {metadata.location && (
          <span className="metadata-item">{metadata.location}</span>
        )}
      </Space>
    );
  }

  // Student metadata
  if (
    resource.resource_type === 'PILOT' ||
    resource.resource_type === 'PASSENGER' ||
    resource.resource_type === 'PERSON'
  ) {
    return (
      <Space direction="vertical" size={0}>
        {metadata.license_type && (
          <Tag color="purple">{metadata.license_type}</Tag>
        )}
        {metadata.hours_logged && (
          <span className="metadata-item">
            <strong>Hours:</strong> {metadata.hours_logged}
          </span>
        )}
      </Space>
    );
  }

  return null;
};

/**
 * Get columns configuration based on resource type
 */
const getColumns = (
  _resourceType: ResourceType, // Reserved for future type-specific columns
  onEdit?: (resource: Resource) => void,
  onDelete?: (resource: Resource) => void,
  onToggleAvailability?: (resource: Resource) => void
): ColumnsType<Resource> => {
  const baseColumns: ColumnsType<Resource> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
      sorter: true,
    },
    {
      title: 'Type',
      dataIndex: 'resource_type',
      key: 'resource_type',
      width: '12%',
      render: (type: ResourceType) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: 'Details',
      key: 'metadata',
      width: '25%',
      render: (_: any, record: Resource) => formatMetadata(record),
    },
    {
      title: 'Availability',
      dataIndex: 'is_available',
      key: 'is_available',
      width: '12%',
      render: (isAvailable: boolean, record: Resource) => (
        <Tooltip
          title={
            !isAvailable && record.availability_notes
              ? record.availability_notes
              : null
          }
        >
          {isAvailable ? (
            <Tag icon={<CheckCircleOutlined />} color="success">
              Available
            </Tag>
          ) : (
            <Tag icon={<CloseCircleOutlined />} color="error">
              Unavailable
            </Tag>
          )}
        </Tooltip>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: '18%',
      ellipsis: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '10%',
      render: (_: any, record: Resource) => (
        <Space>
          {onEdit && (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(record);
              }}
            />
          )}
          {onToggleAvailability && (
            <Button
              type="link"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggleAvailability(record);
              }}
            >
              {record.is_available ? 'Disable' : 'Enable'}
            </Button>
          )}
          {onDelete && (
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(record);
              }}
            />
          )}
        </Space>
      ),
    },
  ];

  return baseColumns;
};

export const ResourceTable: React.FC<ResourceTableProps> = ({
  resourceType,
  resources,
  loading = false,
  onEdit,
  onDelete,
  onToggleAvailability,
}) => {
  const columns = getColumns(
    resourceType,
    onEdit,
    onDelete,
    onToggleAvailability
  );

  return (
    <div className="resource-table">
      <Table
        columns={columns}
        dataSource={resources}
        loading={loading}
        rowKey="id"
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};
